/**
 * Thin analytics wrapper: PostHog (product analytics + session replay,
 * the primary behavioral/context layer) + GA4/GTM (web acquisition
 * measurement) + Google Ads (via GTM, once conversion actions exist).
 *
 * All three are consent-gated. The loaders in public/index.html only fire
 * scripts after the user opts in. This module offers a single capture()
 * API for the app to use.
 *
 * Event ownership: `capture()`/`pushDataLayerEvent()` are for BEHAVIORAL
 * events (clicks, navigation) — never a canonical business fact on their
 * own. Business events (e.g. Audit completion) must only be fired from a
 * call site that already holds backend-confirmed state (see ResultsRoom.jsx).
 */

import { ROUTES } from "../config/links";
import { getAnalyticsConsentState } from "../hooks/useConsent";

const CONSENT_KEY = "iag_consent_v1";
// Same GA4 property configured in public/index.html's consent-gated loader.
const GA_MEASUREMENT_ID = "G-N3ZJMFS5CL";
// Inbound attribution-capture foundation — set once (first-touch, never
// overwritten) by public/index.html's promoteAttributionContext().
const ATTRIBUTION_KEY = "iag_attribution_v1";
// InterAct Tracking Foundation milestone (2026-10-10) — the SAME
// per-tab-session landing snapshot `public/index.html`'s
// `captureLandingSnapshot()` already writes unconditionally on every
// hard page load (gclid/utm_*/referrer/landing_page/link_id/oppref/...,
// captured BEFORE any first-touch promotion decision). Read-only here
// — this file never writes to it — exposed as this request's "current
// session touch" (see `getSessionTouch()` below), reusing the exact
// existing capture rather than a second one.
const LANDING_SNAPSHOT_KEY = "iag_landing_snapshot_v1";
// InterAct Tracking Foundation milestone (2026-10-10) — UNLIKE
// `ATTRIBUTION_KEY` (first-touch, write-once), this key is
// UNCONDITIONALLY OVERWRITTEN by `public/index.html`'s
// `captureLandingSnapshot()` every time a landing snapshot actually
// carries a non-direct signal (any gclid/gbraid/wbraid/utm_*/oppref/
// referrer/link_id) — "the last time this browser had ANY non-direct
// signal", distinct from the immutable first touch and from the
// current session's own (possibly direct) entry. Never cleared/expired
// by age — a reporting window is a query-time concern, never a storage
// expiry (see `conversion_snapshot.py`'s matching backend rule).
const LAST_NONDIRECT_TOUCH_KEY = "iag_last_nondirect_touch_v1";
// InterAct Tracking Foundation milestone (2026-10-10) — the stable,
// opaque, first-party browser-context id carried through CTA
// navigation and the shared request adapter (see
// `buildAttributionRequestContext()` below). Created ONCE per browser
// (persisted, never regenerated, never tied to an expiry) and NEVER
// used as a Contact/person lookup key anywhere — see
// `conversion_snapshot.py`'s module docstring for why that separation
// matters. Deliberately a DIFFERENT key/value than PostHog's own
// distinct_id or `ATTRIBUTION_KEY`'s first-touch snapshot — this is
// purely a journey-correlation id, never attribution evidence or a
// verified identity on its own.
const BROWSER_CONTEXT_KEY = "iag_browser_context_v1";

const hasAnalyticsConsent = () => {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    return !!JSON.parse(raw)?.analytics;
  } catch {
    return false;
  }
};

/**
 * Derived purely from the URL path prefix (matching the same /pt/ and /el/
 * convention Nav.jsx and useLang() already use) — never a new locale
 * source. Lets plain functions in this module (not React components)
 * attach a `language` property without every call site having to pass
 * one through.
 */
const getCurrentLanguage = () => {
  if (typeof window === "undefined") return "en";
  const m = window.location.pathname.match(/^\/(pt|el)(?=\/|$)/);
  return m ? m[1] : "en";
};

/**
 * Locale-independent page identifier — the same pathname every
 * trackCta() call site already resolves (page_path), with the /pt/ or
 * /el/ prefix stripped (same regex as getCurrentLanguage() above) and
 * the trailing slash removed, collapsing to "/" for the homepage.
 * Sitewide source-attribution ("which page did this click come from")
 * for every CTA — homepage, service pages, tools, guides, resources,
 * about, articles, everything — without any page/component having to
 * pass its own identifier (every CTA already funnels through
 * trackCta() below, so this one helper covers the whole site).
 * `article_slug` (passed explicitly by article CTAs) stays alongside
 * this as a convenience alias; page_slug is the universal field.
 */
export const getPageSlug = () => {
  if (typeof window === "undefined") return null;
  const stripped = window.location.pathname.replace(/^\/(pt|el)(?=\/|$)/, "") || "/";
  return stripped.length > 1 ? stripped.replace(/\/$/, "") : stripped;
};

export const capture = (event, props = {}) => {
  if (!hasAnalyticsConsent()) return;
  try {
    // Bug fix (2026-10-12) — every consent-eligible journey event
    // (cta_click, asset_viewed, form_*, ...) now carries the SAME
    // stable first-party `context_id` `buildAttributionRequestContext()`
    // already sends on accepted requests, so PostHog can correlate an
    // earlier click/view with a later event from the SAME browser
    // (e.g. a booking CTA click with the canonical consultation_booked
    // event) without ever calling identify()/alias() — this is a plain
    // EVENT PROPERTY, never a person-merge. A caller-supplied
    // `context_id` (none today) always wins over the auto-attached one.
    const contextId = props.context_id ?? getBrowserContextId() ?? undefined;
    const enrichedProps = contextId ? { ...props, context_id: contextId } : props;
    if (window.posthog?.capture) window.posthog.capture(event, enrichedProps);
    if (window.gtag) window.gtag("event", event, enrichedProps);
  } catch {
    /* no-op */
  }
};

export const identify = (id, traits = {}) => {
  if (!hasAnalyticsConsent()) return;
  try {
    if (window.posthog?.identify) window.posthog.identify(id, traits);
  } catch {
    /* no-op */
  }
};

/**
 * Clears PostHog's local identity (back to a fresh anonymous
 * distinct_id) — the counterpart to identify() above. Used on
 * authenticated Rehearsal Room logout only, so a second person on a
 * SHARED DEVICE never inherits the previous person's identified
 * PostHog identity. Never gated on consent — clearing local state is
 * always safe, and if PostHog was never loaded `window.posthog` is
 * simply absent (optional chaining handles it).
 */
export const resetIdentity = () => {
  try {
    if (window.posthog?.reset) window.posthog.reset();
  } catch {
    /* no-op */
  }
};

export const pageview = (path) => {
  if (!hasAnalyticsConsent()) return;
  try {
    if (window.gtag) window.gtag("event", "page_view", { page_path: path });
    if (window.posthog?.capture) window.posthog.capture("$pageview", { $current_url: path });
  } catch {
    /* no-op */
  }
};

/**
 * Stable, non-visible-text destination classification for a CTA href —
 * "Use stable identifiers... not visible button text" (see PRD). Based
 * only on the existing route config, never invented new routes.
 */
const BOOKING_ROUTES = [
  ROUTES.booking,
  ROUTES.bookPresentation,
  ROUTES.bookExecutivePresence,
  ROUTES.bookCorporate,
  ROUTES.bookLdPartnership,
];

// Service identifier (for analytics `service` properties) derived purely
// from a /book/* href — never a new destination, just a stable label for
// the existing route config above.
export const serviceFromBookingHref = (href) => {
  if (href === ROUTES.bookPresentation) return "presentation";
  if (href === ROUTES.bookExecutivePresence) return "executive_presence";
  if (href === ROUTES.bookCorporate) return "corporate";
  if (href === ROUTES.bookLdPartnership) return "ld_partnership";
  return null;
};

export const classifyCtaDestination = (href) => {
  if (!href) return "other";
  if (BOOKING_ROUTES.includes(href)) return "booking";
  if (href === ROUTES.contact) return "contact";
  if (href === ROUTES.highStakesAudit || href.includes("/diagnostics")) return "audit";
  if (href.includes("/insights/guides")) return "guide";
  if (
    href.startsWith(ROUTES.executiveCoaching) ||
    href.startsWith(ROUTES.corporateTraining) ||
    href.startsWith(ROUTES.ldPartnerships)
  ) {
    return "programme";
  }
  return "other";
};

// Service identifier for a CTA landing on one of the 3 programme info
// pages (NOT their own /book/* variants above, which already have a
// registered asset_id and resolve via `resolveTargetAssetId` instead).
// Never a new destination, just a stable label for the existing route
// config — mirrors `serviceFromBookingHref`'s own naming convention.
const programmeServiceFromHref = (href) => {
  if (!href) return null;
  if (href.startsWith(ROUTES.executiveCoaching)) return "executive_coaching";
  if (href.startsWith(ROUTES.corporateTraining)) return "corporate_training";
  if (href.startsWith(ROUTES.ldPartnerships)) return "ld_partnership";
  return null;
};

/**
 * Reverse lookup against the SAME `asset_registry.json` `canonical_route`
 * every registered asset (diagnostics, the Presenter Playbook guide,
 * `contact_request`, and the 4 `booking_*` entries) already declares —
 * never a second mapping to keep in sync by hand. `href` passed into
 * `trackCta` is always the canonical, unlocalized route already (see
 * CTA.jsx — it tracks the original `href` prop, not the locale-prefixed
 * `to`), but this strips a `/pt`/`/el` prefix defensively anyway, in
 * case a future caller ever passes a localized one. Returns `null` for
 * any href that isn't a registered asset's own `canonical_route` (a
 * static informational page, for example) — never invented.
 */
const resolveTargetAssetId = (href) => {
  if (!href) return null;
  const path = href.replace(/^\/(pt|el)(?=\/|$)/, "") || "/";
  const match = assetRegistry.assets.find((a) => a.canonical_route === path);
  return match ? match.asset_id : null;
};

let _pageInstancePath = null;
let _pageInstanceId = null;
/**
 * One stable id per LOGICAL page view — changes whenever the resolved
 * pathname changes, including an SPA client-side route transition, not
 * only a hard reload — so a `cta_click` can distinguish "this click
 * happened during THIS particular visit to this page" from an earlier
 * or later visit to the SAME page later in the same tab session.
 * Module-level, in-memory only — never persisted, never a durable or
 * cross-session id.
 */
const getPageInstanceId = () => {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname;
  if (_pageInstancePath !== path) {
    _pageInstancePath = path;
    _pageInstanceId = generateLocalId();
  }
  return _pageInstanceId;
};

/**
 * 2026-10-09 correction — `iag_last_cta_source` is a one-shot signal
 * ONLY valid for the page view that immediately follows the CTA click
 * that set it. Without a freshness check, a stale value left over from
 * an earlier, unrelated navigation in the same tab session would get
 * incorrectly attributed to a LATER, direct/bookmarked visit — i.e.
 * inventing a CTA source that didn't actually precede this view. 15s
 * comfortably covers a real click-then-navigate, not a later revisit.
 *
 * 2026-10-09 clarification (does NOT change behavior) — this 15s rule
 * governs ONLY this narrow, event-level, same-navigation `cta_source`
 * signal (sessionStorage, tab-scoped, one-shot-consumed). It is a
 * DIFFERENT, SEPARATE mechanism from this site's actual longer-term
 * acquisition attribution, which is `getAttributionContext()` below
 * (localStorage, `first_touch`, immutable once captured, retained per
 * `attribution_policy` — see `tracking_registry.json`) and
 * `posthog_delivery_adapter.classify_attribution_evidence()`'s
 * `attribution_evidence` tiers server-side. Neither this freshness gate
 * nor `consumeFreshCtaSource()`'s clear-on-read ever touches, shortens,
 * or clears that separate, durable store — they use entirely different
 * storage keys and this file never writes to `ATTRIBUTION_KEY`. Do not
 * "fix" a longer-term attribution gap by loosening this 15s value; use
 * `getAttributionContext()`/`attribution_evidence` for that instead.
 */
const CTA_SOURCE_FRESHNESS_MS = 15000;
// InterAct Tracking Foundation milestone (2026-10-10) — `extra` carries
// the SAME CTA navigation context (cta_id/content_id/target_asset_id/
// run_id) onward to the shared request adapter (see
// `buildAttributionRequestContext()`), reusing this exact one-shot
// stash rather than a second, parallel one. `value` (the existing
// `cta_source` string) is unchanged for every pre-existing reader.
const stashCtaSource = (value, extra = {}) => {
  try { sessionStorage.setItem("iag_last_cta_source", JSON.stringify({ value, ...extra, ts: Date.now() })); } catch { /* no-op */ }
};
const consumeFreshCtaSource = () => {
  let result = null;
  try {
    const raw = sessionStorage.getItem("iag_last_cta_source");
    sessionStorage.removeItem("iag_last_cta_source");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Date.now() - parsed.ts <= CTA_SOURCE_FRESHNESS_MS) result = parsed;
    }
  } catch { /* no-op — malformed/legacy plain-string value is treated as absent, never invented */ }
  return result;
};

/**
 * Single structured CTA-click tracker — always fires `cta_click` with
 * stable properties, and additionally fires the existing `book_call_click`
 * (PostHog + GTM data layer) when the destination is the booking route.
 * Never a semantic duplicate of `book_call_click` — same event name,
 * reused everywhere a CTA can reach booking.
 *
 * Also fires `audit_cta_clicked` (Phase 5) whenever the destination is the
 * Audit — same pattern as `book_call_click`, just for the Audit funnel
 * step. `cta_location` is expected to be one of a small controlled set
 * (hero, nav dropdown names like coaching_dropdown/insights_dropdown,
 * explainer_page, service_page_cta, exit-intent, footer, audit_results,
 * sticky_cta) rather than arbitrary free text — see call sites.
 *
 * InterAct Tracking Foundation milestone (2026-10-09 pass 8) — this is
 * the ONE shared helper every article/content template, tool/guide
 * card, and commercial CTA component (Nav, Footer, FinalCTA,
 * ReadinessCheckBanner, ExecutivePresenceDiagnosticBanner, StickyBookCTA,
 * ExitIntentPrompt, StickyWhatsApp, CTA.jsx's 4 variants) already routes
 * through — no second, per-component `cta_click` publisher was added.
 * `contentId` (new, optional — a translated version of the SAME
 * article/case-study/tool/guide must pass the SAME stable id regardless
 * of `language`; for this CMS, that's simply the article/case-study's
 * own `slug` — never language-specific — or a tool/guide card's static
 * `id`) defaults to `articleSlug` when not explicitly given, so every
 * existing `articleSlug`-passing call site gets `content_id` for free.
 * `page_instance_id`/`canonical_path` are always auto-derived — no
 * caller passes them.
 *
 * 2026-10-09 pass 9 correction — `targetAssetId`/`targetService` are now
 * EXPLICIT, caller-supplied params, not auto-derived. The content/call-
 * site configuration is the AUTHORITATIVE source — a conversion CTA's
 * own `location` (placement) and `targetAssetId`/`targetService` are set
 * where it is rendered (or, for CMS content, in the content schema; see
 * docs/adding_a_new_tracked_asset.md §8). `resolveTargetAssetId()`'s
 * reverse `href`→`canonical_route` lookup is kept only as a CONVENIENCE
 * FALLBACK for call sites that have not been retrofitted yet, or whose
 * `href` is dynamic (e.g. a CMS article's own `cta_href`) — never silently
 * trusted over an explicit value, and never assumed reliable for a
 * localized, aliased, or dynamic href (string concatenation, a `#anchor`,
 * an admin-entered URL) that may not match a registered `canonical_route`
 * at all. `target_resolution` on the emitted event records which happened:
 * `"explicit"` (caller supplied it), `"route_fallback"` (resolved from
 * `href` only, no explicit value given — lower confidence), or
 * `"unresolved"` (neither — see the dev-only console.warn below).
 */
export const trackCta = ({
  ctaId,
  location = null,
  href,
  pagePath,
  articleSlug = null,
  contentId = null,
  targetAssetId: explicitTargetAssetId = null,
  targetService: explicitTargetService = null,
  // InterAct Tracking Foundation milestone (2026-10-10) — an already-
  // existing diagnostic run_id PRESERVED through this click (e.g. a
  // results-page CTA to Contact/booking), never fabricated for a CTA
  // with no run behind it. Carried onward via the same CTA-source
  // stash as cta_id/content_id/target_asset_id below.
  runId = null,
} = {}) => {
  const resolvedPagePath = pagePath || (typeof window !== "undefined" ? window.location.pathname : null);
  const pageSlug = getPageSlug();
  const destinationType = classifyCtaDestination(href);
  const language = getCurrentLanguage();
  const fallbackTargetAssetId = resolveTargetAssetId(href);
  const fallbackTargetService = fallbackTargetAssetId ? null : programmeServiceFromHref(href);
  const hasExplicitTarget = Boolean(explicitTargetAssetId || explicitTargetService);
  const targetAssetId = explicitTargetAssetId ?? fallbackTargetAssetId;
  const targetService = explicitTargetAssetId ? null : (explicitTargetService ?? fallbackTargetService);
  const targetResolution = hasExplicitTarget ? "explicit" : (targetAssetId || targetService ? "route_fallback" : "unresolved");
  const pageInstanceId = getPageInstanceId();
  const resolvedContentId = contentId ?? articleSlug;
  // InterAct Tracking Foundation milestone (2026-10-09, pass 8 + pass 9
  // correction) — a lightweight, dev-only completeness guard: "a newly
  // published conversion CTA with missing target or placement is
  // flagged" (see docs/adding_a_new_tracked_asset.md). Never throws,
  // never runs in production, never blocks the click — purely a console
  // signal for whoever is adding the new CTA/asset. The repository ALSO
  // has a static, CI-enforced version of the "missing placement" half of
  // this check (backend/tests/test_asset_registry_completeness.py).
  if (process.env.NODE_ENV !== "production") {
    if (!location) {
      // eslint-disable-next-line no-console
      console.warn(`[tracking] cta_click fired without a placement (ctaId="${ctaId}") — pass location= so this CTA is attributable to a specific placement on the page.`);
    }
    if (destinationType !== "other" && targetResolution === "unresolved") {
      // eslint-disable-next-line no-console
      console.warn(`[tracking] cta_click to a "${destinationType}" destination (href="${href}") has no explicit targetAssetId/targetService AND no resolvable route fallback. Pass one explicitly at the call site (or, for CMS content, in its content schema) — do not rely on an ambiguous/localized/dynamic href resolving on its own.`);
    }
    // "flag... conflicting metadata" — an explicit target that disagrees
    // with what the href itself would resolve to is a content bug (the
    // CTA's declared destination and its actual `href` have drifted
    // apart), never silently trusted either way.
    if (explicitTargetAssetId && fallbackTargetAssetId && explicitTargetAssetId !== fallbackTargetAssetId) {
      // eslint-disable-next-line no-console
      console.warn(`[tracking] cta_click explicit targetAssetId="${explicitTargetAssetId}" conflicts with href="${href}", which resolves to "${fallbackTargetAssetId}" via canonical_route. Fix the explicit value or the href — one of them is wrong.`);
    }
  }
  capture("cta_click", {
    cta_id: ctaId || null,
    cta_location: location,
    placement: location,
    page_path: resolvedPagePath,
    page_slug: pageSlug,
    canonical_path: pageSlug,
    content_id: resolvedContentId,
    destination_type: destinationType,
    target_asset_id: targetAssetId,
    target_service: targetService,
    target_resolution: targetResolution,
    page_instance_id: pageInstanceId,
    language,
    article_slug: articleSlug,
  });
  if (destinationType === "booking") {
    const service = serviceFromBookingHref(href);
    stashCtaSource(ctaId || location || "unknown", { cta_id: ctaId || null, content_id: resolvedContentId, target_asset_id: targetAssetId, run_id: runId });
    capture("book_call_click", { cta_id: ctaId || null, cta_location: location, page_path: resolvedPagePath, page_slug: pageSlug, language, service, article_slug: articleSlug });
    pushDataLayerEvent("book_call_click", { cta_id: ctaId || null, cta_location: location, service, page_slug: pageSlug, article_slug: articleSlug });
    captureOaiqEvent("booking_cta_clicked");
    // "A booking category selected" — only meaningful when the click
    // actually picks one of the four dedicated services (not the generic
    // /book selection page itself).
    if (service) {
      capture("book_category_selected", { cta_id: ctaId || null, cta_location: location, page_path: resolvedPagePath, page_slug: pageSlug, language, service, article_slug: articleSlug });
    }
  }
  if (destinationType === "audit") {
    stashCtaSource(ctaId || location || "unknown", { cta_id: ctaId || null, content_id: resolvedContentId, target_asset_id: targetAssetId, run_id: runId });
    capture("audit_cta_clicked", { cta_id: ctaId || null, cta_location: location, page_path: resolvedPagePath, page_slug: pageSlug, language, article_slug: articleSlug });
  }
  // InterAct Tracking Foundation milestone (2026-10-10) — the Contact
  // form gets the SAME "preserve the actual clicked CTA source through
  // navigation" treatment booking/audit/guide already had, reusing the
  // identical stash call — consumed by Contact.jsx's own
  // `trackAssetViewed("contact_request", ...)` at mount.
  if (destinationType === "contact") {
    stashCtaSource(ctaId || location || "unknown", { cta_id: ctaId || null, content_id: resolvedContentId, target_asset_id: targetAssetId, run_id: runId });
  }
  // 2026-10-09 pass 8 — the Presenter Playbook guide (and any future
  // registered guide) gets the SAME "preserve the actual clicked
  // source through navigation" treatment booking/audit already had,
  // reusing the identical stash call — consumed by the destination
  // asset's own `trackAssetViewed()` via `consumeFreshCtaSource()`
  // (no change needed there, it is already asset-agnostic).
  if (destinationType === "guide" && targetAssetId) {
    stashCtaSource(ctaId || location || "unknown", { cta_id: ctaId || null, content_id: resolvedContentId, target_asset_id: targetAssetId, run_id: runId });
  }
};

/**
 * Fires once per dedicated/general booking page view — reuses the existing
 * `booking_page_viewed` event name (previously only on /booking) with an
 * added `service` property (`null` on the general /book selection page).
 * Mirrors the exact bookingSource/previousPath/auditCompletedBefore shape
 * Booking.jsx already captured, now shared by every /book* page.
 *
 * Bug fix (2026-10-12) — `ctaNavContext` is OPTIONAL and, when passed,
 * MUST be the value a caller already consumed itself via
 * `consumeFreshCtaSource()`/`trackAssetViewed()` on this SAME page
 * view (e.g. `DedicatedBookingPage.jsx`, which also needs that same
 * one-shot stash for `TidyCalEmbedWidget`'s eventual booking bridge
 * call). Without this param, this function falls back to its
 * original behavior of consuming the stash itself (unchanged for
 * `BookingSelect.jsx`, the general /book page, which has no other
 * consumer). Passing BOTH would drain the one-shot stash twice —
 * whichever call runs second would silently get `null`, exactly the
 * bug this fixes.
 */
export const captureBookingPageViewed = (service = null, ctaNavContext = undefined) => {
  let previousPath = null;
  try {
    if (typeof document !== "undefined" && document.referrer && new URL(document.referrer).origin === window.location.origin) {
      previousPath = new URL(document.referrer).pathname;
    }
  } catch { /* no-op */ }
  const bookingSource = ctaNavContext !== undefined ? (ctaNavContext?.value ?? null) : (consumeFreshCtaSource()?.value ?? null);
  let auditCompletedBefore = false;
  try { auditCompletedBefore = sessionStorage.getItem("iag_audit_completed_v1") === "1"; } catch { /* no-op */ }
  capture("booking_page_viewed", {
    service,
    booking_source: bookingSource,
    previous_path: previousPath,
    audit_completed_before_booking: auditCompletedBefore,
    language: getCurrentLanguage(),
  });
  captureOaiqPageViewed();
};

/**
 * Client-side "booking widget finished" signal for AD-PLATFORM pixels
 * ONLY (Google Ads/GA4 via the GTM data layer + the OpenAI Ads Manager
 * pixel) — fired from the TidyCal embed's own `message` event
 * (`bookingComplete`, origin `https://tidycal.com`, see TidyCal's embed
 * docs). `completedRef` in TidyCalEmbedWidget.jsx dedupes a rapid
 * double-fire from the iframe.
 *
 * Deliberately does NOT call capture() / send anything to PostHog: the
 * CANONICAL `consultation_booked` PostHog event is the backend's own
 * `consultation_booking_reconciliation.py` sweep (TidyCal's own booking
 * list, polled every 5 min, keyed by the canonical contact_id, with
 * dedupe + retries + attribution enrichment) — see CHANGELOG.md
 * "PostHog/TidyCal/diagnostic instrumentation audit". Also sending a
 * second, anonymous-distinct_id, un-deduped "the booking finished" event
 * to PostHog from here would double-count every booking under two
 * different event identities for the same real-world action.
 *
 * Platform-verified attribution gating (read from the SAME first-touch
 * snapshot every other call site already reads via
 * getAttributionContext() — no new capture, no PII):
 * - ChatGPT/OpenAI Ads (`captureOaiqEvent`/`captureOaiqAppointmentScheduled`)
 *   fires ONLY when a verified `oppref` click reference exists for this
 *   visitor. No `oppref`, no ChatGPT Ads conversion — every booking no
 *   longer counts as a ChatGPT Ads conversion by default.
 * - Google Ads: the actual conversion TAG lives in GTM, outside this
 *   repo, so it cannot be gated here directly. Instead this always pushes
 *   `google_ads_attribution_verified` (a plain boolean, never a raw
 *   gclid/gbraid/wbraid value) onto the SAME `consultation_booked`
 *   dataLayer event — the Google Ads conversion tag's trigger in GTM
 *   must require Custom Event `consultation_booked` AND Data Layer
 *   Variable `google_ads_attribution_verified` equals `true` (see
 *   CHANGELOG.md for the exact trigger spec). Until that GTM-side
 *   trigger condition is added, the tag will still fire on every
 *   booking — this is a GTM container change, not something fixable
 *   from code alone.
 * - Meta: intentionally not implemented here — no Meta pixel/CAPI
 *   integration exists in this codebase.
 */
export const captureConsultationBookedAdsSignal = (service, { bookingTypeId, tidycalBookingId, bookingTime } = {}) => {
  const attribution = getAttributionContext();
  const hasOpenAiAttribution = !!attribution?.oppref;
  const hasGoogleAdsAttribution = !!(attribution?.gclid || attribution?.gbraid || attribution?.wbraid);

  pushDataLayerEvent("consultation_booked", {
    service,
    booking_type_id: bookingTypeId || null,
    booking_time: bookingTime || null,
    google_ads_attribution_verified: hasGoogleAdsAttribution,
  });

  if (hasOpenAiAttribution) {
    captureOaiqEvent("consultation_booked");
    // `tidycalBookingId` (TidyCal's own raw booking id, already the
    // same correlation key `consultation_booking_reconciliation.py`
    // stringifies into `provider_booking_id`) is this booking's stable
    // `event_id` for OpenAI's official Pixel ID + event name + event_id
    // dedup contract — distinct from any `lead_created` event_id this
    // same booking may separately generate server-side (see
    // `booking_attribution_bridge.py` / `consultation_booking_
    // reconciliation.py`), so the two standard events stay
    // independently identifiable even though they share one booking.
    captureOaiqAppointmentScheduled(tidycalBookingId ? String(tidycalBookingId) : undefined);
  }
};

/**
 * Reads the first-touch attribution context captured on landing (gclid,
 * utm_*, landing_page, referrer, captured_at, link_id — link_id always
 * null today, reserved for the future Tracking Links module). Returns
 * `null` if never captured (e.g. no analytics consent yet, or a direct/
 * organic visit with nothing to capture). Never blocks/throws.
 */
export const getAttributionContext = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Push a custom event straight onto the GTM data layer — used only for
 * events that a GTM tag (e.g. a future Google Ads conversion tag) needs
 * to trigger on. Does nothing to PostHog; pair with capture() separately
 * if the same event should also be a PostHog behavioral event.
 */
export const pushDataLayerEvent = (event, props = {}) => {
  if (!hasAnalyticsConsent() || typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...props });
  } catch {
    /* no-op */
  }
};

/**
 * Fires a ChatGPT Ads Manager Pixel custom event
 * (`oaiq("measure", "custom", ...)`), consent-gated identically to
 * capture()/pushDataLayerEvent() above. The pixel's documented data shape
 * does NOT accept arbitrary properties (no utm source, gclid, or contact_id
 * support — see index.html's loader comment) — it only accepts
 * `custom_event_name` plus a small fixed set of commerce fields (amount,
 * currency, plan_id, contents), none of which apply to these funnel
 * events, so none are sent. `eventName` must follow OpenAI's custom-event
 * naming rules (1-64 chars, letters/numbers/underscore/dash, lowercase).
 */
export const captureOaiqEvent = (eventName, eventId) => {
  if (!hasAnalyticsConsent() || typeof window === "undefined") return;
  try {
    if (window.oaiq) {
      // Ads conversion events, pass 2 (2026-02) — `eventId` is OPTIONAL,
      // same official `event_id` dedup key (4th-arg, alongside
      // `custom_event_name`) already added to the standard events.
      // Omitted entirely when the caller has none, never fabricated.
      const customArgs = { custom_event_name: eventName };
      if (eventId) customArgs.event_id = eventId;
      window.oaiq("measure", "custom", { type: "custom" }, customArgs);
    }
  } catch {
    /* no-op */
  }
};

/**
 * Ads conversion events, pass 2 (2026-02) — classifies the CURRENT
 * page (NOT the WhatsApp link's destination, which is always just the
 * business's own number) into "presentation" / "executive_presence" /
 * `null` (ambiguous — no WhatsApp conversion custom event should
 * fire), for the two GLOBAL WhatsApp entry points (`StickyWhatsApp`,
 * `Footer`), which have no page-type awareness of their own.
 *
 * Deliberately a SMALL, explicit allow-list — never a guess. Sourced
 * from `assetRegistry.json`'s own authoritative `service_icp` field
 * for the diagnostic/booking routes, plus the 2 dedicated service-
 * landing pages outside that registry. Every other route (home,
 * about, contact, blog/case studies, the `/executive-coaching` hub,
 * both `/corporate-training/*` pages, Presenter Archetype, the Next
 * Presentation Playbook guide — whose OWN registry entry is
 * `general_awareness`, not `presentation_coaching`, despite its
 * asset_id's name — and the retired High-Stakes Audit) resolves to
 * `null` on purpose: only the existing generic `whatsapp_click`
 * PostHog capture fires there, no OpenAI custom event, never both
 * journeys for one click.
 */
const _WHATSAPP_PRESENTATION_ROUTES = new Set([
  "/insights/diagnostics/next-presentation-readiness-check",
  "/insights/diagnostics/presentation-readiness",
  "/book/presentation",
  "/executive-coaching/high-stakes-presentation-coaching",
]);
const _WHATSAPP_EXECUTIVE_PRESENCE_ROUTES = new Set([
  "/insights/diagnostics/executive-presence-communication-diagnostic",
  "/book/executive-presence",
  "/executive-coaching/executive-presence-development",
]);

export const resolveWhatsAppJourney = (pageSlug) => {
  if (_WHATSAPP_PRESENTATION_ROUTES.has(pageSlug)) return "presentation";
  if (_WHATSAPP_EXECUTIVE_PRESENCE_ROUTES.has(pageSlug)) return "executive_presence";
  return null;
};

const _WHATSAPP_JOURNEY_EVENT_NAMES = {
  presentation: "presentation_whatsapp_received",
  executive_presence: "executive_whatsapp_received",
};

/**
 * Fires the matching WhatsApp custom conversion event for a real click
 * on a WhatsApp link, classified by the page the visitor is on. A
 * fresh, per-click `crypto.randomUUID()` is the event_id — there is no
 * stable run/submission id for a one-off click, unlike the diagnostic
 * lifecycle events. Fires AT MOST one event (never both journeys);
 * fires nothing at all on an ambiguous page.
 */
export const captureWhatsAppClickEvent = () => {
  const journey = resolveWhatsAppJourney(getPageSlug());
  if (!journey) return;
  const eventName = _WHATSAPP_JOURNEY_EVENT_NAMES[journey];
  const eventId = `${eventName}:${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
  captureOaiqEvent(eventName, eventId);
};

/**
 * Fires the Ads Manager STANDARD `page_viewed` event for the /booking
 * page's conversion signal — exact call per spec, no custom_event_name,
 * no extra properties: `oaiq("measure", "page_viewed", { type: "contents" })`.
 * Consent-gated identically to captureOaiqEvent(); the `window.oaiq` guard
 * also ensures this never fires before the base Pixel's own `init` call
 * has already been queued (loadOaiq() defines `window.oaiq` synchronously
 * as part of granting consent, before any page other than the very first
 * load could reach this call).
 */
export const captureOaiqPageViewed = () => {
  if (!hasAnalyticsConsent() || typeof window === "undefined") return;
  try {
    if (window.oaiq) {
      window.oaiq("measure", "page_viewed", { type: "contents" });
    }
  } catch {
    /* no-op */
  }
};

/**
 * Fires the Ads Manager STANDARD `appointment_scheduled` conversion
 * event — exact call per OpenAI's spec, no custom_event_name, no extra
 * properties: `oaiq("measure", "appointment_scheduled", { type:
 * "customer_action" })`. This is the one call ChatGPT Ads Manager
 * actually recognizes as a billable/optimizable conversion action for
 * a booked consultation call — distinct from `captureOaiqEvent(
 * "consultation_booked")`, which only logs a generic `custom` event
 * under OAIQ's own event name, not a named standard conversion. Fired
 * from `captureConsultationBookedAdsSignal()` above, alongside (not
 * instead of) the existing canonical-named custom event and the
 * server-authoritative PostHog `consultation_booked` — never on its
 * own, so there is exactly one place a real booking completion signal
 * originates from client-side.
 */
export const captureOaiqAppointmentScheduled = (eventId) => {
  if (!hasAnalyticsConsent() || typeof window === "undefined") return;
  try {
    if (window.oaiq) {
      // OpenAI documented dedup contract: Pixel ID + event name +
      // event_id. Passed as the 4th argument's `event_id` key, exactly
      // like `custom_event_name` above. `eventId` is OPTIONAL — omit
      // the 4th arg entirely when the caller has none, never fabricate
      // one. The attribution gate (`hasOpenAiAttribution` in
      // `captureConsultationBookedAdsSignal`) is UNCHANGED here —
      // tracking-audit follow-up (2026-02) deliberately left it as-is
      // pending a separate approval.
      if (eventId) {
        window.oaiq("measure", "appointment_scheduled", { type: "customer_action" }, { event_id: eventId });
      } else {
        window.oaiq("measure", "appointment_scheduled", { type: "customer_action" });
      }
    }
  } catch {
    /* no-op */
  }
};

/**
 * Fires the Ads Manager STANDARD `lead_created` conversion event —
 * exact call per OpenAI's spec: `oaiq("measure", "lead_created", {
 * type: "customer_action" })`. Used for the diagnostic's completion
 * (a real email/contact capture — the textbook definition of a lead),
 * REPLACING the prior generic `custom_event_name: "diagnostic_completed"`
 * call — `lead_created` is the correct, billable/optimizable standard
 * event for this action, so it's the only one sent, not an addition.
 *
 * Lead-created tracking audit (2026-02) — deliberately NOT gated on
 * `getAttributionContext().oppref`: per OpenAI's documented Measurement
 * Pixel behavior, the Pixel itself auto-captures `oppref` into its own
 * first-party cookie and Ads Manager performs attribution — an
 * app-level attribution gate here would just suppress legitimate,
 * qualifying conversions for non-ChatGPT-referred visitors, which is
 * explicitly NOT the desired behavior for this event (confirmed by
 * product decision). `eventId` is OPTIONAL (official `event_id` dedup
 * key, 4th-arg `{event_id}`, matched on Pixel ID + event name +
 * event_id) — each call site passes its own already-existing stable
 * id (run_id / requestId / submission_id); never fabricate one here.
 */
export const captureOaiqLeadCreated = (eventId) => {
  if (!hasAnalyticsConsent() || typeof window === "undefined") return;
  try {
    if (window.oaiq) {
      if (eventId) {
        window.oaiq("measure", "lead_created", { type: "customer_action" }, { event_id: eventId });
      } else {
        window.oaiq("measure", "lead_created", { type: "customer_action" });
      }
    }
  } catch {
    /* no-op */
  }
};

/**
 * Resolves to the GA4 client_id ONLY when analytics consent has been
 * granted — otherwise resolves to `null`. Never blocks/throws; safe to
 * await from any submit handler (e.g. the Audit results form's hidden
 * `ga_client_id` field — see ResultsRoom.jsx).
 *
 * GA4 (G-N3ZJMFS5CL) is configured via the pre-existing GTM tag inside
 * GTM-K9KFLXQM (direct gtag.js load consolidated away — verified via
 * live test that GTM alone still exposes a working `window.gtag` for
 * this same measurement ID, see PRD). `window.__gaClientId` is an
 * additional, currently-unused fast path reserved for a future GTM
 * Custom HTML tag that reads the built-in "Client ID" variable.
 */
export const getGaClientId = () =>
  new Promise((resolve) => {
    if (!hasAnalyticsConsent() || typeof window === "undefined") {
      resolve(null);
      return;
    }
    if (window.__gaClientId) {
      resolve(window.__gaClientId);
      return;
    }
    if (!window.gtag) {
      resolve(null);
      return;
    }
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value || window.__gaClientId || null);
    };
    try {
      window.gtag("get", GA_MEASUREMENT_ID, "client_id", finish);
      setTimeout(() => finish(null), 800);
    } catch {
      finish(null);
    }
  });

/**
 * PostHog's own distinct_id, ONLY when analytics consent has been
 * granted — otherwise `null`. Same consent contract as getGaClientId()
 * above; used wherever a hidden form field wants PostHog's distinct_id
 * (e.g. the diagnostic's start_run/complete attribution — see
 * NextPresentationReadinessCheck/api.js) without ever sending the
 * cookieless, pre-consent, daily-rotating id as if it were a stable
 * visitor identity.
 */
export const getPosthogDistinctId = () => {
  if (!hasAnalyticsConsent() || typeof window === "undefined") return null;
  try {
    return window.posthog?.get_distinct_id?.() || null;
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------
 * InterAct Tracking Foundation milestone (2026-10-09) — shared P0
 * event-contract helpers (Tracking Source of Truth v1.1 §3). Every
 * NEW asset should use these instead of hand-rolling its own
 * capture() calls — see /docs/adding_a_new_tracked_asset.md.
 *
 * These are ADDITIVE: no existing event name (form_started,
 * form_submitted, cta_click, audit_started/audit_completed,
 * consultation_booked, book_call_click, ...) is renamed or removed.
 * ------------------------------------------------------------------ */
import assetRegistry from "../config/assetRegistry.json";

const ALLOWLISTED_FORM_FAILURE_CODES = [
  "validation_error", "network_error", "server_error", "rate_limited", "unknown",
];

const getAssetMeta = (assetId) => assetRegistry.assets.find((a) => a.asset_id === assetId) || null;

/** Per-mount, per-attempt, per-run random id — never a PII value, never reused across page loads. */
const generateLocalId = () => {
  try {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* no-op */ }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const generateFormAttemptId = () => generateLocalId();
export const generateDiagnosticRunId = () => generateLocalId();

/**
 * Fires once per asset page view — the P0 `asset_viewed` event. Looks
 * up asset_type/asset_version/service_icp from `assetRegistry.json` so
 * call sites never hand-type metadata that can drift from the
 * registry. `placement` is free text describing WHERE on the page
 * (e.g. "hero", "full_page") only when relevant to the asset's own
 * layout — most assets can omit it. `cta_source` is read ONCE from the
 * same `iag_last_cta_source` sessionStorage slot `trackCta()` stashes
 * for booking/audit-destination clicks — cleared immediately on read,
 * and only honored if set within the last `CTA_SOURCE_FRESHNESS_MS`
 * (a real preceding click-then-navigate, never a stale leftover value
 * from an earlier, unrelated visit in this tab session) — `null` for
 * a direct visit, a refresh, a stale/expired value, or any CTA this
 * helper doesn't tag (e.g. a plain in-page nav link). Never invented.
 *
 * InterAct Tracking Foundation milestone (2026-10-10) — RETURNS the
 * full consumed CTA-navigation context (`{value, cta_id, content_id,
 * target_asset_id, run_id}`, or `null`), not just the `cta_source`
 * string, so the calling page component can hold onto it (e.g. in a
 * ref at mount) and feed `cta_id`/`content_id`/`target_asset_id`/
 * `run_id` into `buildAttributionRequestContext()` later at submit
 * time — this stash is one-shot/consumed-on-read, so it must be
 * captured here, at mount, not re-read later.
 */
export const trackAssetViewed = (assetId, { placement = null } = {}) => {
  const meta = getAssetMeta(assetId);
  const navContext = consumeFreshCtaSource();
  capture("asset_viewed", {
    asset_id: assetId,
    asset_type: meta?.asset_type || null,
    asset_version: meta?.asset_version || null,
    service_icp: meta?.service_icp || null,
    placement,
    cta_source: navContext?.value ?? null,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    page_slug: getPageSlug(),
    language: getCurrentLanguage(),
  });
  return navContext;
};

/**
 * CTA click carrying full asset/destination context — a thin,
 * registry-aware sibling of `trackCta()` above for CTAs that point AT
 * a registered asset (as opposed to `trackCta()`'s generic
 * booking/audit-only destination classification). Does not replace
 * `trackCta()`; call BOTH if a CTA is both a registered-asset link and
 * needs the existing booking/audit side-effects.
 *
 * 2026-10-09 pass 8 note (no callers today, confirmed by repo search —
 * left in place, not deleted, in case something already depends on it
 * dynamically): `trackCta()` itself now resolves `target_asset_id`
 * from `href` internally (reverse `asset_registry.json` lookup), so a
 * NEW call site should prefer plain `trackCta()` alone over adding a
 * second call to this function — calling both would otherwise emit
 * TWO `cta_click` events (different payload shapes) for one click.
 */
export const trackAssetCtaClick = ({ ctaId, destinationAssetId, placement = null } = {}) => {
  const destMeta = getAssetMeta(destinationAssetId);
  capture("cta_click", {
    cta_id: ctaId || null,
    destination_asset_id: destinationAssetId || null,
    destination_type: destMeta?.asset_type || null,
    placement,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    page_slug: getPageSlug(),
    language: getCurrentLanguage(),
  });
};

/**
 * Fires on the FIRST real field mutation in a registered form —
 * tracking_registry.json: "First field mutation without value
 * capture" (NOT merely because the form rendered/a prior stage
 * completed). `formAttemptId` (generate with `generateFormAttemptId()`
 * at that same first mutation, reuse it for the submit/failure calls
 * of this same attempt) is `null` for existing callers that haven't
 * been migrated to the first-mutation trigger yet — `form_attempt_id`
 * is a required property per the registry; a `null` here is an
 * honest, known gap, never a fabricated id. `assetVariant` (2026-10-09
 * — e.g. "generic"/"personalized" for the Presenter Playbook request,
 * shared by two entry points) and `runId` (2026-10-09 — PRESERVE an
 * already-existing diagnostic run_id when this form follows one;
 * NEVER fabricate one for a caller with no run at all) both default
 * to `null` for every existing caller.
 */
export const trackFormStarted = ({ formId, assetId = null, assetVariant = null, formAttemptId = null, runId = null } = {}) => {
  capture("form_started", {
    form_id: formId, asset_id: assetId, asset_variant: assetVariant, form_attempt_id: formAttemptId,
    run_id: runId, page_slug: getPageSlug(), language: getCurrentLanguage(),
  });
};

/**
 * Fires when the form is DISPATCHED to the backend (before the
 * response is known) — never implies success. Two distinct ids per
 * tracking_registry.json: `formAttemptId` is the ONE id for the whole
 * lifetime of this form-filling attempt (the same value `form_started`
 * fired with — generate it ONCE, at the first real field mutation,
 * never regenerate it per submit click); `attemptId` is a FRESH id for
 * THIS specific submit click (generate with `generateFormAttemptId()`
 * again right before dispatching) so a resubmission after a failure
 * is distinguishable from the first attempt while still correlating
 * back to the same `formAttemptId` session. `trackFormSubmissionFailed`
 * for this same click must reuse the SAME `attemptId` (not `formAttemptId`).
 * `assetVariant`/`runId` — see `trackFormStarted` above.
 */
export const trackFormSubmitAttempted = ({ formId, assetId = null, assetVariant = null, formAttemptId = null, attemptId, runId = null } = {}) => {
  capture("form_submit_attempted", {
    form_id: formId, asset_id: assetId, asset_variant: assetVariant, form_attempt_id: formAttemptId,
    attempt_id: attemptId, run_id: runId, page_slug: getPageSlug(), language: getCurrentLanguage(),
  });
};

/**
 * Fires only once the backend/network call for this attempt has
 * concretely failed. `errorCode` MUST be one of
 * ALLOWLISTED_FORM_FAILURE_CODES — never a raw error message/stack
 * (could leak PII/internal detail into PostHog). `formAttemptId`/
 * `attemptId` — see `trackFormSubmitAttempted` above; `attemptId` must
 * be the SAME value that call used for this exact submit click.
 * `assetVariant`/`runId` — see `trackFormStarted` above.
 */
export const trackFormSubmissionFailed = ({ formId, assetId = null, assetVariant = null, formAttemptId = null, attemptId, runId = null, errorCode } = {}) => {
  const safeCode = ALLOWLISTED_FORM_FAILURE_CODES.includes(errorCode) ? errorCode : "unknown";
  capture("form_submission_failed", {
    form_id: formId, asset_id: assetId, asset_variant: assetVariant, form_attempt_id: formAttemptId,
    attempt_id: attemptId, run_id: runId, error_code: safeCode, page_slug: getPageSlug(), language: getCurrentLanguage(),
  });
};

/** Fires once when a diagnostic run begins. `runId` should be the backend `run_id` once known; pass a locally-generated id (`generateDiagnosticRunId()`) for client-only diagnostics (e.g. Presenter Archetype) that have no backend run. */
export const trackDiagnosticStarted = ({ assetId, runId } = {}) => {
  capture("diagnostic_started", { asset_id: assetId, run_id: runId, page_slug: getPageSlug(), language: getCurrentLanguage() });
};

/** Fires once the visitor has answered every scored question (before the optional contact-capture step, if any). */
export const trackDiagnosticQuestionsCompleted = ({ assetId, runId } = {}) => {
  capture("diagnostic_questions_completed", { asset_id: assetId, run_id: runId, page_slug: getPageSlug(), language: getCurrentLanguage() });
};

/**
 * 2026-10-09 correction — this is NOT a free-standing visibility
 * mirror. `diagnostic_completed`'s idempotency_scope is
 * `diagnostic_id + run_id` — exactly ONE canonical event per run,
 * full stop. For `next_presentation_readiness` and
 * `executive_presence_communication`, the backend's own
 * `routers/diagnostic_routes.py complete_run()` already emits the
 * authoritative `diagnostic_completed` outbox event (dedupe_key
 * `diagnostic_completed:{run_id}`) immediately after
 * `runs.complete_run` persists — calling this helper for either of
 * those two diagnostics would be a SECOND publisher of the same
 * canonical event and is a bug, not a feature. Only call this for an
 * asset whose completion has NO backend owner at all (e.g. Presenter
 * Archetype's `completion_mode=local_result`, scored entirely in the
 * browser) — i.e. this helper IS the sole, authoritative publisher
 * for that asset, never a parallel one alongside a backend emitter.
 */
export const trackDiagnosticCompleted = ({ assetId, runId } = {}) => {
  capture("diagnostic_completed", { asset_id: assetId, run_id: runId, page_slug: getPageSlug(), language: getCurrentLanguage() });
};

/** Fires when the results screen actually renders to the visitor. */
export const trackResultsViewed = ({ assetId, runId } = {}) => {
  capture("results_viewed", { asset_id: assetId, run_id: runId, page_slug: getPageSlug(), language: getCurrentLanguage() });
};

/* ------------------------------------------------------------------
 * InterAct Tracking Foundation milestone (2026-10-10) — shared
 * browser-to-backend attribution context for the Contact form, both
 * diagnostics, and the Presenter Playbook guide request. Reuses every
 * existing capture mechanism above (`getAttributionContext()`'s
 * first-touch, the CTA-source stash, `getAnalyticsConsentState()`) —
 * nothing here is a second/parallel tracker.
 * ------------------------------------------------------------------ */

/**
 * Stable, opaque, first-party browser-context id — created ONCE per
 * browser (localStorage, persisted, never regenerated, never age-
 * expired) and carried through CTA navigation + the shared request
 * adapter below. NEVER used anywhere in this codebase as a Contact/
 * person lookup key — see `conversion_snapshot.py`'s module docstring
 * for the matching backend-side guarantee.
 */
export const getBrowserContextId = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(BROWSER_CONTEXT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.context_id) return parsed.context_id;
    }
    const contextId = generateLocalId();
    window.localStorage.setItem(BROWSER_CONTEXT_KEY, JSON.stringify({ context_id: contextId, created_at: new Date().toISOString() }));
    return contextId;
  } catch {
    return null;
  }
};

/**
 * The CURRENT tab session's landing snapshot — reads the SAME
 * `iag_landing_snapshot_v1` sessionStorage key `public/index.html`'s
 * `captureLandingSnapshot()` already writes unconditionally on every
 * hard page load. Read-only; this file never writes to it. `null` for
 * a session that never captured one (shouldn't happen post-landing,
 * but never fabricated if genuinely absent).
 */
export const getSessionTouch = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LANDING_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * The last time THIS browser's landing carried any non-direct signal
 * (a click reference, a UTM, a referrer, or a Tracking Link id) —
 * UNLIKE first touch, this IS overwritten on every later non-direct
 * landing (see `public/index.html`'s matching write). `null` only
 * when this browser has never had one.
 */
export const getLastNonDirectTouch = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LAST_NONDIRECT_TOUCH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Freshly computed, right now, from the CURRENT URL + referrer — the
 * touch AT THE MOMENT of this conversion, distinct from `getSessionTouch()`
 * (captured once, at this session's entry, which may differ if the
 * visitor arrived on one page/link and converts after browsing on to
 * another within the same tab session). Never persisted anywhere;
 * computed fresh on every call. Same field shape as the other 3
 * touches so the backend can treat all 4 uniformly.
 */
export const getConversionTouchNow = () => {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      gclid: params.get("gclid") || null,
      gbraid: params.get("gbraid") || null,
      wbraid: params.get("wbraid") || null,
      utm_source: params.get("utm_source") || null,
      utm_medium: params.get("utm_medium") || null,
      utm_campaign: params.get("utm_campaign") || null,
      utm_content: params.get("utm_content") || null,
      utm_term: params.get("utm_term") || null,
      utm_id: params.get("utm_id") || null,
      landing_page: window.location.pathname,
      referrer: (typeof document !== "undefined" && document.referrer) || null,
      link_id: params.get("link_id") || null,
      click_id: params.get("_tlc") || null,
      fbclid: params.get("fbclid") || null,
      li_fat_id: params.get("li_fat_id") || null,
      ttclid: params.get("ttclid") || null,
      oppref: params.get("oppref") || null,
      captured_at: new Date().toISOString(),
    };
  } catch {
    return null;
  }
};

/**
 * The ONE shared request-adapter payload builder for the Contact
 * form, both diagnostics, and the Presenter Playbook guide request —
 * "send only the context needed by each request": every field is
 * `undefined` (dropped by `JSON.stringify`) rather than null/empty
 * when not applicable, so a caller with nothing to add (e.g. a
 * generic Playbook request with no CTA/run behind it) sends a
 * minimal object, never a fabricated one.
 *
 * `ctaNavContext` is the object `trackAssetViewed()` RETURNS at
 * mount (one-shot — capture it into a ref there, pass it in here
 * later at submit time; do not call `trackAssetViewed()` again to
 * try to re-read it). `runId` is an already-existing diagnostic run
 * id to PRESERVE (own run_id for a diagnostic's own completion, or a
 * quiz/diagnostic run_id carried forward into a Contact/Playbook
 * submission that followed one) — never fabricated when there is none.
 */
export const buildAttributionRequestContext = ({ ctaNavContext = null, runId = null } = {}) => ({
  context_id: getBrowserContextId() || undefined,
  source_content_id: ctaNavContext?.content_id || undefined,
  cta_id: ctaNavContext?.cta_id || undefined,
  asset_id: ctaNavContext?.target_asset_id || undefined,
  run_id: runId || ctaNavContext?.run_id || undefined,
  first_touch: getAttributionContext() || undefined,
  session_touch: getSessionTouch() || undefined,
  last_non_direct_touch: getLastNonDirectTouch() || undefined,
  conversion_touch: getConversionTouchNow() || undefined,
});

/**
 * InterAct Tracking Foundation milestone (2026-10-10, pass 14) — fires
 * once, from `TidyCalEmbedWidget.jsx`'s existing `bookingComplete`
 * `message` listener (which already validates `event.origin ===
 * "https://tidycal.com"` and `event.data.event === "bookingComplete"`
 * before calling this), to let the backend correlate THIS browser's
 * attribution context with TidyCal's own `providerBookingId` once the
 * backend's own TidyCal reconciliation later confirms the booking —
 * see `backend/booking_attribution_bridge.py` for the full contract.
 *
 * Pass 16 — also sends the SAME EXPLICIT, already-decided analytics-
 * consent state `Contact.jsx`/`NextPresentationReadinessCheck/api.js`/
 * `playbookApi.js` already send (`getAnalyticsConsentState()` —
 * `null` when the visitor has not yet decided, never fabricated).
 * This is the ONLY consent-capture path for a visitor who books
 * directly (no Contact/diagnostic/Playbook request ever submitted) —
 * without it, the backend's analytics-consent projection check would
 * find nothing recorded for that contact FOREVER, regardless of what
 * the visitor actually chose.
 *
 * Best-effort and NEVER blocking/throwing: this is a fire-and-forget
 * signal, not part of the booking-confirmation UX itself. Uses
 * `navigator.sendBeacon` (survives the tab/iframe being torn down
 * right after `bookingComplete` fires) when available, falling back
 * to a `keepalive` fetch.
 *
 * Disabled by default server-side (`BOOKING_ATTRIBUTION_BRIDGE_ENABLED`)
 * — this call is harmless and always safe to make; the backend simply
 * no-ops while the feature is off.
 *
 * Bug fix (2026-10-12) — `ctaNavContext` is the SAME one-shot object
 * `trackAssetViewed()` returns at mount (the dedicated booking page
 * must capture it into a ref there and pass it in here, exactly like
 * Contact/diagnostic/Playbook already do at their own submit time —
 * see `buildAttributionRequestContext()`'s own docstring). Without
 * it, `cta_id`/`source_content_id`/`asset_id` were always sent as
 * `undefined` even though the backend bridge has always been able to
 * persist them — the explicit booking CTA (e.g. which of the 4
 * BookingSelect cards was clicked) never reached the canonical
 * booking's `browser_cta_id`/`browser_cta_target_asset_id` fields.
 * `context_id` itself is UNCHANGED by this fix (it was already sent
 * unconditionally) — only the CTA-specific fields were missing.
 */
export const sendBookingAttributionBridge = (providerBookingId, ctaNavContext = null) => {
  if (typeof window === "undefined" || !providerBookingId) return;
  try {
    const payload = JSON.stringify({
      provider_booking_id: String(providerBookingId),
      source: "tidycal_embed_booking_complete",
      context: buildAttributionRequestContext({ ctaNavContext }),
      analytics_consent: getAnalyticsConsentState(),
    });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/booking/attribution-bridge`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => { /* best-effort */ });
    }
  } catch { /* best-effort — never blocks/breaks the booking-confirmation UX */ }
};

